import { useContext, useEffect, useState as reactUseState } from 'react';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { ReactComponent as SearchClearIcon } from '@assets/svg/search-clear.svg';
import { ReactComponent as SearchIcon } from '@assets/svg/table-search.svg';
import { ReactComponent as DownIcon } from '@assets/svg/table-down.svg';
import { ReactComponent as UpIcon } from '@assets/svg/table-up.svg';
import Rolling from '@assets/gif/rolling.gif';

import {
  IOnboardingFile,
  IPaginationResultFiles,
} from '@models/Onboarding.model';
import { differenceInSeconds, format } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { InfiniteData } from 'react-query';
import { useInView } from 'react-intersection-observer';
import { useState } from '@hookstate/core';
import { GlobalMenu, ITooltip } from '@components/index';
import { NavigationContext } from '@layout/navigation/navigation';
import { ManualAdd } from '@pages/Products/components';
import { TruncatedTextTooltip } from '@components/Tooltip';
import DetectableOverflow from 'react-detectable-overflow';
import {
  EmptyOnboarding,
  ErrorOnboarding,
  ImportInventory,
  OnboardingPercentage,
  RequestStatus,
} from './components';

import { TableContentLoading } from './Onboarding.loading';
import { OnboardingContext } from './Onboarding.query';
import {
  Container,
  Content,
  Header,
  Title,
  Line,
  Heading,
  Spacing,
  LineText,
  ColumnName,
  ColumnContainer,
  Buttons,
  Direction,
  NoText,
  Item,
  TitleContainer,
  Description,
  LoadMore,
  LoadingText,
  LoadingImg,
  InputContainer,
  SearchIconContainer,
  InputSearch,
  SearchButton,
  SearchClear,
  LineTextContainer,
  LineClick,
  MenuContainer,
  PercentageContainer,
  GlobalMenuContainer,
  RequestStatusContainer,
  ItemContainer,
  ErrorMessageContainer,
  ErrorMessage,
  ErrorMessageIcon,
} from './Onboarding.styles';
import { FileQuickActionMenu } from './components/FileQuickActionMenu/FileQuickActionMenu';
import { ImportInvoice } from './components/ImportInvoice/ImportInvoice';

interface IFillOrEmpty {
  label: string | number | undefined | null;
  icon?: any;
  isTruncated?: boolean;
  dataFor?: string;
}

export const FillOrEmpty = ({
  label,
  icon = '',
  isTruncated = false,
  dataFor,
}: IFillOrEmpty) => {
  const [isOverflowed, setIsOverflowed] = reactUseState(false);

  if (label === '' || label === undefined) {
    return <NoText>Empty</NoText>;
  }

  return (
    <LineTextContainer>
      {icon && <BlueIcon icon={icon} color="#8F95B2" />}

      {isTruncated ? (
        <DetectableOverflow onChange={setIsOverflowed}>
          <LineText
            data-hover={isOverflowed}
            data-for={dataFor}
            data-tip={label}
          >
            {label}
          </LineText>
        </DetectableOverflow>
      ) : (
        <LineText limitOfLines={1}>{label}</LineText>
      )}

      {isOverflowed && <TruncatedTextTooltip id={dataFor} />}
    </LineTextContainer>
  );
};

interface IColumnTitle {
  label: string;
  direction?: 'up' | 'down';
  onClick?: () => void;
}

export const ColumnTitle = ({ label, direction, ...rest }: IColumnTitle) => {
  return (
    <ColumnContainer {...rest}>
      <ColumnName>{label}</ColumnName>
      {!!direction && (
        <Direction>{direction === 'up' ? <UpIcon /> : <DownIcon />}</Direction>
      )}
    </ColumnContainer>
  );
};

interface IOnboardingRaw {
  filesQuery?: InfiniteData<IPaginationResultFiles<IOnboardingFile>>;
  fetchNextPage: () => void;
  isError?: boolean;
  isLoading?: boolean;
}

const OnboardingRaw = ({
  filesQuery,
  fetchNextPage,
  isError,
  isLoading,
}: IOnboardingRaw) => {
  const { openImportInventory, search, openImportInvoice, openQuickAdd } =
    useContext(OnboardingContext);

  const { isSuperAdmin } = useContext(NavigationContext);

  const { ref, inView } = useInView();

  const history = useHistory();

  const hasMore = filesQuery
    ? !filesQuery.pages[filesQuery.pages.length - 1].isLastPage
    : false;

  const files = filesQuery
    ? filesQuery.pages
        .map(pageData => pageData.files)
        .reduce(function reduce(prev, next) {
          return prev.concat(next);
        })
    : [];

  useEffect(() => {
    if (inView && hasMore) fetchNextPage();
  }, [inView, hasMore]);

  const searchLocal = useState<string>('');
  const searchEdited = useState<boolean>(false);

  const searchState = search.get() !== searchLocal.get() || searchEdited.get();
  const clearState =
    search.get() === searchLocal.get() &&
    searchLocal.get() !== '' &&
    !searchEdited.get();

  const callSearch = () => {
    search.set(searchLocal.get());
    searchEdited.set(false);
  };

  const clearSearch = () => {
    searchLocal.set('');
    search.set('');
    searchEdited.set(false);
  };

  return (
    <Container>
      <Header>
        <TitleContainer>
          <Title>Onboarding</Title>
          <Description>
            Here is where you’ll be able to track, review and onboard your
            assets
          </Description>
        </TitleContainer>
        <Buttons>
          <InputContainer>
            <SearchIconContainer>
              <SearchIcon />
            </SearchIconContainer>
            <InputSearch
              placeholder="Search"
              value={searchLocal.get()}
              onChange={e => {
                searchLocal.set(e.target.value);
                searchEdited.set(true);

                searchLocal.get().length < 1 && clearSearch();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  callSearch();
                }
              }}
              search={searchState}
              clear={clearState}
            />
            {searchState && (
              <SearchButton onClick={callSearch}>Search</SearchButton>
            )}
            {clearState && (
              <SearchClear onClick={clearSearch}>
                <SearchClearIcon />
              </SearchClear>
            )}
          </InputContainer>

          <GlobalMenuContainer>
            <GlobalMenu
              itemList={[
                {
                  iconName: 'plus',
                  label: 'Quick Add',
                  onClick: () => {
                    openQuickAdd.set(true);
                  },
                },
                {
                  iconName: 'panel-table',
                  label: 'Spreadsheet',
                  onClick: () => {
                    openImportInventory.set(true);
                  },
                },
                {
                  iconName: 'document',
                  label: 'Invoice',
                  onClick: () => {
                    openImportInvoice.set(true);
                  },
                },
              ]}
              menuIcon="plus"
              hoverColor="#fff"
              align="end"
              title="Importers"
              height="40px"
              width="40px"
              paddingTop="9px"
            />
          </GlobalMenuContainer>
        </Buttons>
      </Header>

      <div />

      {filesQuery && files.length === 0 ? (
        <EmptyOnboarding
          title="Nothing to onboard yet!"
          message="Your onboarding actions will show here once initiated."
        />
      ) : (
        <Spacing>
          <Heading data-issuperadmin={isSuperAdmin}>
            <ColumnTitle label="Source" />
            <ColumnTitle label="User" />
            <ColumnTitle label="Total Assets" />
            <ColumnTitle label="Total Unique Products" />
            <ColumnTitle label="Unidentified" />
            <ColumnTitle label="Identified" />
            <ColumnTitle label="Request" />
            <ColumnTitle label="Internal" />
            <ColumnTitle label="Date" />
            <ColumnTitle label="Status" />
          </Heading>
        </Spacing>
      )}

      {filesQuery && (
        <Content>
          {files.map(file => {
            const completed = differenceInSeconds(new Date(), file.createdAt);

            const fileUploadStage = Number(file.etaText?.substring(0, 1));

            const fileCompletionPercentage = (fileUploadStage / 7) * 100;

            const isInProgress =
              file.etaText !== '7. Done' && file.status === 'importing';

            const disableTooltip = () => {
              if (
                isInProgress ||
                file.status === 'aborted' ||
                file.status === 'deleted'
              ) {
                return false;
              }

              return true;
            };

            const tooltipText = () => {
              if (isInProgress) {
                return 'Cannot open file in progress';
              }

              if (file.status === 'aborted') {
                return 'Cannot open aborted file';
              }

              if (file.status === 'deleted') {
                return 'Cannot open deleted file';
              }

              return 'Cannot open this file';
            };

            const FileContent = () => {
              if (file.status === 'aborted') {
                return (
                  <ErrorMessageContainer data-issuperadmin={isSuperAdmin}>
                    <ErrorMessageIcon>
                      <BlueIcon icon="error" color="#D14343" />
                    </ErrorMessageIcon>
                    <ErrorMessage>{file.statusText || 'Error'}</ErrorMessage>
                  </ErrorMessageContainer>
                );
              }

              if (isInProgress) {
                return (
                  <PercentageContainer data-issuperadmin={isSuperAdmin}>
                    <OnboardingPercentage
                      elapsedTime={completed}
                      estimatedTime={file.eta}
                      fileUploadStageText={file.etaText}
                    />
                  </PercentageContainer>
                );
              }

              return (
                <>
                  <FillOrEmpty label={file.totalItems} />
                  <FillOrEmpty label={file.uniqueAssetCounts.totalItems} />
                  <FillOrEmpty
                    label={file.uniqueAssetCounts.unidentifiedItems}
                  />
                  <FillOrEmpty label={file.uniqueAssetCounts.identifiedItems} />
                  <FillOrEmpty label={file.uniqueAssetCounts.requestItems} />
                  <FillOrEmpty label={file.uniqueAssetCounts.inventoryItems} />
                </>
              );
            };

            const fileIcon = () => {
              if (
                [
                  'text/csv',
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                ].includes(file.mimetype)
              ) {
                return 'panel-table';
              }

              if (
                [
                  'application/x-zip-compressed',
                  'application/zip',
                  'application/pdf',
                ].includes(file.mimetype)
              ) {
                return 'document';
              }

              return 'plus';
            };

            return (
              <ItemContainer key={file.id}>
                <Item
                  disabled={isInProgress || file.status !== 'open'}
                  data-for={`onboarding-file-${file.id}`}
                  data-tip={tooltipText()}
                >
                  <Line>
                    <LineClick
                      onClick={() => {
                        if (!isInProgress && file.status === 'open') {
                          history.push(`/onboarding/${file.fileId}`);
                        }
                      }}
                      data-issuperadmin={isSuperAdmin}
                    >
                      <FillOrEmpty
                        label={file.originalname}
                        icon={fileIcon()}
                        isTruncated
                        dataFor={file.id}
                      />
                      <FillOrEmpty label={file.user?.triggeredBy} />

                      <FileContent />

                      <FillOrEmpty
                        label={format(file.createdAt, 'MMM d, yyyy, p')}
                      />
                      <RequestStatusContainer>
                        <RequestStatus
                          status={file.status}
                          progress={fileCompletionPercentage}
                        />
                      </RequestStatusContainer>
                    </LineClick>
                    {file.status === 'open' && !file.isManual && (
                      <MenuContainer>
                        <FileQuickActionMenu file={file} />
                      </MenuContainer>
                    )}
                  </Line>
                </Item>

                <ITooltip
                  id={`onboarding-file-${file.id}`}
                  getContent={() => {
                    return tooltipText();
                  }}
                  disable={disableTooltip()}
                />
              </ItemContainer>
            );
          })}
          <LoadMore ref={ref}>
            {hasMore ? (
              <LoadMore>
                <LoadingText>Loading more</LoadingText>
                <LoadingImg src={Rolling} alt="loading" />
              </LoadMore>
            ) : (
              <div />
            )}
          </LoadMore>
        </Content>
      )}

      {isLoading && <TableContentLoading />}

      {isError && <ErrorOnboarding message="Error on loading files" />}

      {openImportInventory.get() && (
        <ImportInventory
          open={openImportInventory.get()}
          onClose={() => openImportInventory.set(false)}
          files={files}
        />
      )}
      {openImportInvoice.get() && (
        <ImportInvoice
          open={openImportInvoice.get()}
          onClose={() => openImportInvoice.set(false)}
        />
      )}
      {openQuickAdd.get() && (
        <ManualAdd
          open={openQuickAdd.get()}
          onClose={() => {
            openQuickAdd.set(false);
          }}
          isOnboarding
        />
      )}
    </Container>
  );
};

export { OnboardingRaw };
